import { UserHeaderSection } from '@/pages/UserApplications/components/UserHeaderSection/UserHeaderSection';
import { Link, useLocation } from 'react-router-dom';
import logo from '@/assets/icons/logo-100x22-violet.svg';
import { useUnit } from 'effector-react';
import { $user } from '@/models/user';
import classnames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { ROUTES } from '@/config/routes';
import { UserGroup } from '@/types/groups';
import { SvgIcon } from '@/components/UI/Common/SvgIcon';
import { useHasUnreadMessages } from '@/queries/chats';
import { BRAND_PRIMARY } from '@/config/theme';
import {
  MAIN_NAVIGATION_ITEMS,
  isExternalNavigationPath,
  PORTAL_HOME_URL,
  type NavigationItem,
} from './mainNavigation';
import stylesFromModule from './Header.module.scss';

const styles = stylesFromModule as Record<string, string>;

const MENU_BUTTON_WIDTH = 40;
const MENU_BUTTON_GAP = 8;

const NavItemLink = ({
  item,
  pathname,
  className,
  onClick,
}: {
  item: NavigationItem;
  pathname: string;
  className?: string;
  onClick?: () => void;
}) => {
  const isActive = !isExternalNavigationPath(item.path) && pathname === item.path;
  const linkClassName = classnames(styles.navLink, isActive && styles.active, className);

  if (isExternalNavigationPath(item.path)) {
    return (
      <a href={item.path} className={linkClassName} onClick={onClick}>
        {item.title}
      </a>
    );
  }

  return (
    <Link
      to={item.path}
      className={linkClassName}
      aria-current={isActive ? 'page' : undefined}
      onClick={onClick}
    >
      {item.title}
    </Link>
  );
};

const HeaderNotificationsLink = () => {
  const { pathname } = useLocation();
  const hasUnreadMessages = useHasUnreadMessages();
  const isActive = pathname === ROUTES.CHATS.BASE || pathname.startsWith(`${ROUTES.CHATS.BASE}/`);

  return (
    <Link
      to={ROUTES.CHATS.BASE}
      aria-label="Уведомления"
      className={classnames(
        'inline-flex shrink-0 transition-colors duration-200',
        isActive ? 'text-brand-primary' : 'text-gray hover:text-brand-primary',
      )}
    >
      <SvgIcon
        size={40}
        name={hasUnreadMessages ? 'notification_unread' : 'notification'}
        color={isActive ? BRAND_PRIMARY : 'currentColor'}
      />
    </Link>
  );
};

export const Header: React.FC = () => {
  const { keycloak } = useKeycloak();
  const user = useUnit($user);
  const { pathname } = useLocation();
  const navigationItems = MAIN_NAVIGATION_ITEMS;
  const canSeeNotifications = Boolean(
    keycloak.authenticated && user?.groups?.some((group) => group === UserGroup.Notifications),
  );

  const headerInnerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLAnchorElement>(null);
  const userAreaRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileNavRef = useRef<HTMLElement>(null);

  const [visibleCount, setVisibleCount] = useState(navigationItems.length);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const recalculateVisibleCount = useCallback(() => {
    const headerInner = headerInnerRef.current;
    const logoEl = logoRef.current;
    const userArea = userAreaRef.current;
    const measureNav = measureRef.current;

    if (!headerInner || !logoEl || !userArea || !measureNav) {
      return;
    }

    const measureLinks = Array.from(measureNav.children) as HTMLElement[];
    if (measureLinks.length === 0) {
      setVisibleCount(0);
      return;
    }

    const computedStyles = getComputedStyle(headerInner);
    const gap = Number.parseFloat(computedStyles.columnGap || computedStyles.gap || '0') || 0;
    const paddingLeft = Number.parseFloat(computedStyles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(computedStyles.paddingRight) || 0;
    // clientWidth includes padding; flex items live in the content box
    const contentWidth = headerInner.clientWidth - paddingLeft - paddingRight;
    const availableWidth = Math.floor(
      contentWidth - logoEl.offsetWidth - userArea.offsetWidth - gap * 2,
    );

    const fitsCount = (maxWidth: number) => {
      let used = 0;
      let count = 0;

      for (const link of measureLinks) {
        // ceil so a partially-fitting item never counts as fitting
        const next = used + Math.ceil(link.getBoundingClientRect().width);
        if (next > maxWidth) {
          break;
        }
        used = next;
        count += 1;
      }

      return count;
    };

    let count = fitsCount(availableWidth);

    if (count < measureLinks.length) {
      count = fitsCount(availableWidth - MENU_BUTTON_WIDTH - MENU_BUTTON_GAP);
    }

    setVisibleCount(count);
  }, []);

  useLayoutEffect(() => {
    recalculateVisibleCount();
  }, [navigationItems, canSeeNotifications, recalculateVisibleCount]);

  useEffect(() => {
    const headerInner = headerInnerRef.current;
    if (!headerInner) {
      return;
    }

    const observer = new ResizeObserver(() => {
      recalculateVisibleCount();
    });

    observer.observe(headerInner);
    if (userAreaRef.current) {
      observer.observe(userAreaRef.current);
    }

    window.addEventListener('resize', recalculateVisibleCount);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', recalculateVisibleCount);
    };
  }, [recalculateVisibleCount]);

  useEffect(() => {
    if (visibleCount >= navigationItems.length) {
      setIsMenuOpen(false);
    }
  }, [visibleCount, navigationItems.length]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (mobileNavRef.current?.contains(target) || menuButtonRef.current?.contains(target)) {
        return;
      }
      setIsMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  const visibleItems = navigationItems.slice(0, visibleCount);
  const overflowItems = navigationItems.slice(visibleCount);
  const hasOverflow = overflowItems.length > 0;

  return (
    <header className={styles.header}>
      <div className={styles.headerInner} ref={headerInnerRef}>
        <a ref={logoRef} href={PORTAL_HOME_URL} className={styles.logoLink} aria-label="На главную">
          <img src={logo} alt="Familia" className={styles.logo} />
        </a>

        <div className={styles.navigationArea}>
          <nav className={styles.desktopNavigation} aria-label="Основная навигация">
            {visibleItems.map((item) => (
              <NavItemLink key={item.title} item={item} pathname={pathname} />
            ))}
          </nav>

          <button
            ref={menuButtonRef}
            type="button"
            className={classnames(styles.menuButton, hasOverflow && styles.visible)}
            aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            aria-expanded={isMenuOpen}
            aria-controls="main-navigation-mobile"
            onClick={() => setIsMenuOpen((open) => !open)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div className={styles.userArea} ref={userAreaRef}>
          {canSeeNotifications && <HeaderNotificationsLink />}
          <UserHeaderSection />
        </div>

        <nav
          ref={measureRef}
          className={classnames(styles.desktopNavigation, styles.navigationMeasure)}
          aria-hidden
        >
          {navigationItems.map((item) => (
            <NavItemLink key={item.title} item={item} pathname={pathname} />
          ))}
        </nav>
      </div>

      <nav
        ref={mobileNavRef}
        id="main-navigation-mobile"
        className={classnames(styles.mobileNavigation, isMenuOpen && styles.open)}
        aria-label="Дополнительная навигация"
      >
        {overflowItems.map((item) => (
          <NavItemLink
            key={item.title}
            item={item}
            pathname={pathname}
            onClick={() => setIsMenuOpen(false)}
          />
        ))}
      </nav>
    </header>
  );
};
